import { useState, useEffect } from 'react'
import apiClient from '../services/apiClient'
import { useAuth } from '../auth/AuthContext'
import NoticeBanner from './NoticeBanner'

const EMPTY_FORM = {
  experiment_name: '',
  project_info: '',
  problem_statement: '',
  hypothesis: '',
  ai_approach: '',
  success_criteria: '',
  team_id: '',
  is_active: true,
}

/**
 * ExperimentModal — Create / edit AI Brief modal overlay.
 *
 * Props:
 *   experiment  object | null  — null = create mode; object = edit mode (must have .id)
 *   onSave      fn()           — called after successful save; parent should refresh list
 *   onClose     fn()
 */
export default function ExperimentModal({ experiment, onSave, onClose }) {
  const { user } = useAuth()
  const isEdit = !!experiment?.id
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    team_id: user?.team_id ?? '',
  }))
  const [teams, setTeams]           = useState([])
  const [error, setError]           = useState(null)
  const [saving, setSaving]         = useState(false)
  const [loading, setLoading]       = useState(isEdit)

  // Fetch teams list (always needed for the dropdown)
  useEffect(() => {
    apiClient.get('/admin/teams').then((res) => setTeams(res.data.items ?? []))
  }, [])

  // When editing, fetch the full detail (the list item doesn't have all fields)
  useEffect(() => {
    if (!isEdit) return
    setLoading(true)
    apiClient
      .get(`/admin/experiments/${experiment.id}`)
      .then((res) => {
        const d = res.data
        setForm({
          experiment_name: d.experiment_name ?? '',
          project_info:    d.project_info ?? '',
          problem_statement: d.problem_statement ?? '',
          hypothesis:        d.hypothesis ?? '',
          ai_approach:       d.ai_approach ?? '',
          success_criteria:  d.success_criteria ?? '',
          team_id:           d.team_id ?? '',
          is_active:         d.is_active ?? true,
        })
      })
      .catch(() => setError('Failed to load AI Brief details.'))
      .finally(() => setLoading(false))
  }, [experiment?.id, isEdit])

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.experiment_name.trim()) { setError('AI Brief Name is required.'); return }
    if (!form.team_id) { setError('Team is required.'); return }
    setSaving(true)
    setError(null)
    try {
      if (isEdit) {
        await apiClient.put(`/admin/experiments/${experiment.id}`, {
          ...form,
          team_id: form.team_id ? Number(form.team_id) : null,
        })
      } else {
        await apiClient.post('/admin/experiments', {
          ...form,
          team_id: Number(form.team_id),
        })
      }
      onSave()
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Save failed. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const showDeactivationWarning =
    isEdit && experiment.is_active && (experiment.survey_count ?? 0) > 0 && !form.is_active

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className="modal-box">
        <div className="modal-header">
          <h2 className="modal-title" id="modal-title">
            {isEdit ? 'Edit AI Brief' : 'New AI Brief'}
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {loading && <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>}

        {!loading && (
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-danger">{error}</div>}

            {showDeactivationWarning && (
              <NoticeBanner variant="warning">
                This AI Brief has {experiment.survey_count} survey{experiment.survey_count !== 1 ? 's' : ''} linked
                to it. It will no longer appear in the survey form, but existing data is preserved.
              </NoticeBanner>
            )}

            <Field label="AI Brief Name" required>
              <input
                className="form-control"
                value={form.experiment_name}
                onChange={set('experiment_name')}
                maxLength={255}
                placeholder="e.g. AI-assisted development demo"
              />
            </Field>

            <Field label="Team" required>
              <select
                className="form-control"
                value={form.team_id}
                onChange={set('team_id')}
              >
                <option value="">Select a team…</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Project Info">
              <textarea className="form-control" rows={2} value={form.project_info} onChange={set('project_info')} placeholder="Project context…" />
            </Field>

            <Field label="Problem Statement">
              <textarea className="form-control" rows={2} value={form.problem_statement} onChange={set('problem_statement')} placeholder="What problem is AI being applied to?" />
            </Field>

            <Field label="Hypothesis">
              <textarea className="form-control" rows={2} value={form.hypothesis} onChange={set('hypothesis')} placeholder="Expected outcome…" />
            </Field>

            <Field label="AI Approach">
              <textarea className="form-control" rows={2} value={form.ai_approach} onChange={set('ai_approach')} placeholder="How AI tools are being applied…" />
            </Field>

            <Field label="Success Criteria">
              <textarea className="form-control" rows={2} value={form.success_criteria} onChange={set('success_criteria')} placeholder="How success will be measured…" />
            </Field>

            <div className="form-group">
              <label className="checkbox-item" style={{ fontWeight: 600 }}>
                <input type="checkbox" checked={form.is_active} onChange={set('is_active')} />
                Active
              </label>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save AI Brief'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <div className="form-group">
      <label className={`form-label${required ? ' required' : ''}`}>{label}</label>
      {children}
    </div>
  )
}
